import * as React from "react";
import { graphql } from "gatsby";
import styled, { keyframes } from "styled-components";
import PageContainer from "../pageContainer.js";
import { SectionTitle } from "../typography.js";
import { when } from "../../styles/theme.js";
import { GatsbyImage } from "gatsby-plugin-image";
import { Table, Tbody, Td, Th, Thead, Tr } from "../table.js";
import { useState } from "react";
import { useLocalization } from "gatsby-theme-i18n";
import ToggleButton from "../toggleButton.js";
import { useInView } from "react-intersection-observer";
import { StaticImage } from "gatsby-plugin-image";

const AWARD_ITEM_COUNT = 5;

export const fragment = graphql`
  fragment AwardsContent on ContentfulPageSectionAwards {
    title
    subtitle
    figure
    figureSuffix
    featuredAwards {
      name
      awarder
      year
      logoImage {
        description
        gatsbyImageData(layout: CONSTRAINED, width: 200, height: 200)
      }
    }
    awards {
      name
      awarder
      year
      #      logoImage {
      #        gatsbyImageData
      #      }
    }
    internal {
      type
    }
  }
`;

const translations = {
  en: {
    name: "Prize",
    awarder: "Awarder",
    year: "Year",
  },
  zh: {
    name: "獎項/證書",
    awarder: "頒發單位",
    year: "年份",
  },
};

const marquee = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

const Container = styled(PageContainer)`
  margin-top: 10rem;
  margin-bottom: 10rem;
`;

const Header = styled.div`
  position: relative;
  margin-top: 3rem;
  color: ${(props) => props.theme.colors.white};
  background: ${(props) => props.theme.colors.darkBlue};
  margin-left: -${(props) => props.theme.pageContainerWidth.xs}rem;
  margin-right: -${(props) => props.theme.pageContainerWidth.xs}rem;
  padding-left: ${(props) => props.theme.pageContainerWidth.xs}rem;
  padding-right: ${(props) => props.theme.pageContainerWidth.xs}rem;
  padding-top: 5rem;
  padding-bottom: 23rem;
  overflow: hidden;

  ${when("sm")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.sm}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.sm}rem;
    padding-left: ${(props) => props.theme.pageContainerWidth.sm}rem;
    padding-right: ${(props) => props.theme.pageContainerWidth.sm}rem;
  }

  ${when("md")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.md}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.md}rem;
    padding-left: ${(props) => props.theme.pageContainerWidth.md}rem;
    padding-right: ${(props) => props.theme.pageContainerWidth.md}rem;
  }

  ${when("lg")} {
    margin-left: 0;
    margin-right: 0;
    padding-left: ${(props) => props.theme.pageContainerWidth.md}rem;
    padding-right: ${(props) => props.theme.pageContainerWidth.md}rem;
  }
`;

const HeaderTitle = styled.h3`
  font-size: 2.4rem;
  margin: 0;
`;

const HeaderTitleTop = styled.span`
  display: block;
`;

const HeaderTitleFigure = styled.span`
  font-size: 6rem;
  vertical-align: middle;
  line-height: 1;
  font-weight: 700;
`;

const HeaderTitleFigureSuffix = styled.span`
  //display: inline-block;
  margin-left: 0.5rem;
`;

const HeaderImageContainer = styled.div`
  position: absolute;
  top: 45%;
  right: -5%;
`;

const FeaturedAwardsContainer = styled.div`
  max-width: 100vw;
  overflow: hidden;
  margin-left: -${(props) => props.theme.pageContainerWidth.xs}rem;
  margin-right: -${(props) => props.theme.pageContainerWidth.xs}rem;

  ${when("sm")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.sm}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.sm}rem;
  }

  ${when("md")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.md}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.md}rem;
  }

  ${when("lg")} {
    margin-left: 0;
    margin-right: 0;
  }
`;

const FeaturedAwards = styled.div`
  display: flex;
`;

const FeaturedAwardsSetContainer = styled.div`
  display: flex;
  animation: ${marquee} 15s linear infinite;
`;

const FeaturedAward = styled.div`
  width: 25rem;
  padding: 4rem;
  text-align: center;
`;

const AwardList = styled.div`
  margin-top: 2rem;
`;

const TableContainer = styled.div`
  //margin-top: 5rem;
  margin-left: -${(props) => props.theme.pageContainerWidth.xs}rem;
  margin-right: -${(props) => props.theme.pageContainerWidth.xs}rem;

  ${when("sm")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.sm}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.sm}rem;
  }

  ${when("md")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.md}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.md}rem;
  }

  ${when("lg")} {
    margin-left: 0;
    margin-right: 0;
  }
`;

const Name = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  margin-top: 2rem;
`;

const Awarder = styled.div`
  font-weight: 500;
  margin-top: 1rem;
  font-size: 1.4rem;
`;

const LogoContainer = styled.div`
  width: 12rem;
  height: 12rem;
  border-radius: 999px;
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
  transform: translateZ(0);
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const StyledTh = styled(Th)`
  &:first-child {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }

  &:last-child {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }

  ${when("lg")} {
    &:first-child {
      border-bottom-left-radius: 1rem;
      border-top-left-radius: 1rem;
    }

    &:last-child {
      border-bottom-right-radius: 1rem;
      border-top-right-radius: 1rem;
    }
  }
`;

const FeaturedAwardsSet = ({ featuredAwards }) => {
  return (
    <FeaturedAwardsSetContainer>
      {featuredAwards.map((featuredAward, index) => {
        return (
          <FeaturedAward key={index}>
            {featuredAward.logoImage && (
              <LogoContainer>
                <GatsbyImage
                  alt={featuredAward.logoImage.description}
                  image={featuredAward.logoImage.gatsbyImageData}
                />
              </LogoContainer>
            )}
            <Name>{featuredAward.name}</Name>
            <Awarder>{featuredAward.awarder}</Awarder>
          </FeaturedAward>
        );
      })}
    </FeaturedAwardsSetContainer>
  );
};

const Awards = ({ data, setCurrentSection, index }) => {
  const { locale } = useLocalization();
  const translation = translations[locale];
  const [showAll, setShowAll] = useState(false);

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        setCurrentSection(index);
      }
    },
    rootMargin: "-30% 0% -30% 0%",
  });

  const awards = showAll ? data.awards : data.awards.slice(0, AWARD_ITEM_COUNT);

  return (
    <Container
      as={"section"}
      hasSideBar={true}
      ref={ref}
      id={`section-${index}`}
    >
      <SectionTitle>{data.title}</SectionTitle>
      <Header>
        <HeaderTitle>
          <HeaderTitleTop>{data.subtitle}</HeaderTitleTop>
          <HeaderTitleFigure>{data.figure}</HeaderTitleFigure>
          <HeaderTitleFigureSuffix>{data.figureSuffix}</HeaderTitleFigureSuffix>
        </HeaderTitle>
        <HeaderImageContainer>
          <StaticImage
            src="../../images/background_pattern.png"
            alt=""
            width={450}
            height={450}
            placeholder={"none"}
          />
        </HeaderImageContainer>
      </Header>
      <FeaturedAwardsContainer>
        <FeaturedAwards>
          <FeaturedAwardsSet featuredAwards={data.featuredAwards} />
          <FeaturedAwardsSet featuredAwards={data.featuredAwards} />
        </FeaturedAwards>
      </FeaturedAwardsContainer>
      <AwardList>
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <StyledTh width={"40%"}>{translation.name}</StyledTh>
                <StyledTh width={"40%"}>{translation.awarder}</StyledTh>
                <StyledTh width={"20%"}>{translation.year}</StyledTh>
              </Tr>
            </Thead>
            <Tbody>
              {awards.map((award, index) => {
                return (
                  <Tr key={index}>
                    <Td>{award.name}</Td>
                    <Td>{award.awarder}</Td>
                    <Td>{award.year}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
        <ButtonContainer>
          <ToggleButton isActive={showAll} setIsActive={setShowAll} />
        </ButtonContainer>
      </AwardList>
    </Container>
  );
};

export const awardsName = "ContentfulPageSectionAwards";

export default Awards;
